import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useContext } from "react";
import { AuthContext } from "@/contexts/AuthContext";

export interface OddsEvent {
  id: string;
  homeTeam: string;
  awayTeam: string;
  league: string;
  homeOdds: number | null;
  drawOdds: number | null;
  awayOdds: number | null;
  startTime: string;
}

interface OddsResponse {
  success: boolean;
  odds: OddsEvent[];
  raw?: unknown;
  error?: string;
}

export function useLiveOdds() {
  // Use useContext directly to avoid throwing when outside provider
  const authContext = useContext(AuthContext);
  const session = authContext?.session ?? null;
  const isAuthenticated = !!session?.access_token;

  return useQuery({
    queryKey: ["liveOdds", isAuthenticated],
    queryFn: async (): Promise<OddsEvent[]> => {
      if (!isAuthenticated) {
        return [];
      }

      console.log("Fetching live odds from Bet365 API...");
      
      const { data, error } = await supabase.functions.invoke<OddsResponse>("bet365-odds", {
        body: {},
      });

      if (error) {
        console.error("Error fetching odds:", error);
        throw new Error(error.message || "Failed to fetch odds");
      }

      if (!data?.success || !data?.odds) {
        console.warn("No odds data received:", data);
        return [];
      }

      console.log(`Received ${data.odds.length} odds events`);
      return data.odds;
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 60000 : false,
    staleTime: 30000,
    retry: 2,
  });
}
